'use client';

import React, {FC} from 'react';
import {usePathname, useSearchParams} from 'next/navigation';

import {renderUtils} from '@zoefin/design-system';
import {useAsyncStorageValue} from '@zoefin/hooks';
import {request} from '@zoefin/request';

import {Affiliates} from '@/utils/affiliates/utils';

import DynamicStorage from '../../../utils/dynamicStorage';
import {
  ASSETS_OPTION_VALUES,
  CORE_ASSETS,
  HOUSEHOLD_INCOME_OPTIONS,
} from '../../commons/FormsLibrary/constants';
import {
  CoreAsset,
  FormFields,
  StoredFormFields,
} from '../../commons/FormsLibrary/types';
import getSkipProp from '../../commons/FormsLibrary/utils/getSkipProp';
import logTrackingEvent from '../../commons/FormsLibrary/utils/logTrackingEvent';
import {
  prepareFromRead,
  prepareToSave,
} from '../../commons/FormsLibrary/utils/prepareDataToSaveRead';

import layout from './components/layout';
import {Form} from './form';
import structure from './structure';
import {handleNext, onSubmit, validators} from './utils';

const ASSETS_DEFAULT = ASSETS_OPTION_VALUES[0].value;

/*
 * Only the slider defaults, all of which are numbers — so this is assignable to
 * both the in-memory and the persisted form shape, which differ only in the two
 * Set-valued fields. Typed by the keys it sets rather than as one of the two.
 */
const INITIAL_VALUES: Pick<
  FormFields,
  | 'household_income'
  | 'assets_cash'
  | 'assets_real_state'
  | 'assets_investments'
  | 'assets_retirement'
  | 'assets_other'
> = {
  // Defaults for slider steps
  household_income: HOUSEHOLD_INCOME_OPTIONS[0].value,
  assets_cash: ASSETS_DEFAULT,
  assets_real_state: ASSETS_DEFAULT,
  assets_investments: ASSETS_DEFAULT,
  assets_retirement: ASSETS_DEFAULT,
  assets_other: ASSETS_DEFAULT,
};

/*
 * Typed with the persisted shape, not the in-memory one: what actually lands in
 * storage has the Set fields flattened to arrays, which is why the conversion
 * helpers exist. Declaring it as `Partial<FormFields>` is what forced the
 * `as any` casts inside those helpers.
 */
const FORM_STORAGE = new DynamicStorage<Partial<StoredFormFields>>(
  'new_faaf',
  INITIAL_VALUES,
);

// Overwrite of the `other` option description only (we don't want to change it
// in DB yet) — computed once at module scope since it never varies with props
// or state, replacing what was a mount-time `setCoreAssets` call.
const CORE_ASSETS_WITH_OTHER_DESCRIPTION: CoreAsset[] = (() => {
  const assetsCopy = [...CORE_ASSETS];
  const otherIndex = assetsCopy.findIndex(({id}) => id === 6);

  if (otherIndex !== -1) {
    assetsCopy[otherIndex] = {
      ...assetsCopy[otherIndex],
      description: 'Includes stock options, crypto, collectibles, etc',
    };
  }

  return assetsCopy;
})();

/**
 * Supported query parameters:
 * - skip: To skip specific steps (only `intro` ATM). Supported formats:
 *   - `?skip=intro,goals` or `?skip=intro&skip=goals`
 * - simple_layout: In layout, hides privacy widget, footer, container, and make background white (bankrate)
 * - hosted: Disable self submit, redirects to xxxxxx for a separate form submission
 *
 * Missing:
 *
 * UI
 * - Get welcome modal back (cover Facet change)
 *
 * Logic
 * - Show error message if submit fails
 * - Combine Storage & dynamicStorage
 */

// Module scope so the loop is not part of the component's render body.
function groupSearchParams(
  searchParams: URLSearchParams | null,
): Record<string, string[]> {
  const grouped: Record<string, string[]> = {};

  for (const [key, value] of searchParams ?? new URLSearchParams()) {
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(value);
  }

  return grouped;
}

const Onboarding2_0: FC<{affiliate?: Affiliates}> = ({affiliate}) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const queryParams = groupSearchParams(searchParams);

  const storedValues = useAsyncStorageValue(() => FORM_STORAGE.get());
  const initialValues =
    storedValues && (prepareFromRead(storedValues) || INITIAL_VALUES);

  const skipParams = queryParams.skip ?? [];
  const skip = getSkipProp({
    skip: skipParams.length > 1 ? skipParams : skipParams[0],
  });
  const simpleLayoutParam = queryParams.simple_layout?.[0] ?? null;

  // Force all forms (but Hearst, Moby, and Find Your Advisor) to skip `intro` step
  if (
    pathname !== '/find-your-advisor' &&
    pathname !== '/forms/hearst' &&
    pathname !== '/forms/moby' &&
    !skip.includes('intro')
  ) {
    skip.push('intro');
  }

  // Page view event — no better home than a mount effect for a one-off
  // analytics beacon with nothing to derive, fetch-and-setState, or key off.
  renderUtils.useMount(() => {
    logTrackingEvent();
  });

  // Wait for the persisted draft to load (IndexedDB read via `@zoefin/hooks`'
  // `useAsyncStorageValue`, see above).
  if (!initialValues) {
    return null;
  }

  return (
    <Form
      structure={structure}
      initialValues={initialValues}
      additionalProps={{
        skip,
        coreAssets: CORE_ASSETS_WITH_OTHER_DESCRIPTION,
        // To hide privacy widget, footer, container, and make background white (bankrate)
        simpleLayout: simpleLayoutParam !== null,
        // Hide step icon for mobile and/or desktop
        hideStepIcon: simpleLayoutParam !== null ? ['mobile'] : undefined,
        source: affiliate || pathname,
      }}
      validators={{
        ...validators,
        location: (value) => {
          if (value?.length !== 5) return false;

          return new Promise((resolve) => {
            request(`/api/core-cc/zip_tz?zip_code=${value}`)
              .then(() => resolve(true))
              .catch(() => resolve(false));
          });
        },
      }}
      handleNext={handleNext}
      onChange={(values) => FORM_STORAGE.set(prepareToSave(values))}
      onSubmit={onSubmit}>
      {layout}
    </Form>
  );
};

export default Onboarding2_0;
